/** 人工放行屏的纯逻辑（无 React）。
 *
 * 过期判断归服务端（`overrides[].active`），这里**不比时间**；scope 合法性也以服务端 400 为准（裁决 B4），
 * 前端只把下拉与手填拼成一个 scope 字符串。
 */

import type { OverrideRecord } from "../api/g2";

/** 下拉里「手填标的代码」那一项的取值。 */
export const SCOPE_INSTRUMENT_CODE = "instrument:";

/** 下拉选项：三层层级名 + `strategy:<sid>`（来自 `/paper/state`）+ 手填 `instrument:<代码>`。 */
export function scopeOptions(strategyIds: string[]): { value: string; label: string }[] {
  return [
    { value: "instrument", label: "instrument（标的层，全部标的）" },
    { value: "strategy", label: "strategy（策略层，全部策略）" },
    { value: "portfolio", label: "portfolio（组合层）" },
    ...strategyIds.map((sid) => ({ value: `strategy:${sid}`, label: `strategy:${sid}` })),
    { value: SCOPE_INSTRUMENT_CODE, label: "instrument:<代码>（在下一栏手填）" },
  ];
}

/** 把下拉选择与手填代码拼成请求里的 scope；选了手填项却没填代码返回空串（表单判必填）。 */
export function buildScope(choice: string, instrumentCode: string): string {
  if (choice !== SCOPE_INSTRUMENT_CODE) return choice;
  const code = instrumentCode.trim();
  return code ? `${SCOPE_INSTRUMENT_CODE}${code}` : "";
}

/** `<input type="datetime-local">` 的值按 **UTC** 解释，转成 ISO 8601（带 Z）。不合法返回 `null`。 */
export function localUtcToIso(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? "0")));
  return Number.isNaN(date.getTime()) ? null : date.toISOString().replace(".000Z", "Z");
}

/** 默认有效期：现在 + `hours` 小时，格式化成 datetime-local 用的 UTC 文本。 */
export function defaultExpiry(nowMs: number, hours = 24): string {
  return new Date(nowMs + hours * 3_600_000).toISOString().slice(0, 16);
}

/** 按服务端 `active` 分组；`active` 缺失（旧后端）时全部归入 `unknown`，不自己比时间。 */
export function splitOverrides(rows: OverrideRecord[]): { active: OverrideRecord[]; expired: OverrideRecord[]; unknown: OverrideRecord[] } {
  return {
    active: rows.filter((row) => row.active === true),
    expired: rows.filter((row) => row.active === false),
    unknown: rows.filter((row) => row.active === null),
  };
}

/** 风险报告 `actions[]` 中 `action=override` 的行。 */
export function overrideActions(report: Record<string, unknown>): Record<string, unknown>[] {
  const actions = report["actions"];
  if (!Array.isArray(actions)) return [];
  return actions.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && (item as Record<string, unknown>)["action"] === "override",
  );
}
